import { ref as dbRef, onValue } from 'firebase/database';
import { db } from '../firebase';
import { help } from '../help';
import { playtest } from './playtest';
import { queuedBugReportCount } from './bugReports';

// Everything around the game state that might explain a bug report. Matt,
// 2026-10-10: "make sure that we are always passing in any information that
// might or even remotely could be relevant to any bug report." The full game
// state already rides along as `gameState`; this is the rest - which screen,
// who the reporter is at the table, the connection, recent errors, the
// build. Compact on purpose (no blobs, no tokens, no other people's emails),
// and every piece is gathered inside its own try so a failure here can never
// stop a report from sending.

const MAX_ERRORS = 8;
const recentErrors = [];
let connected = null;
let connectedChangedAt = null;
let installed = false;

const note = (kind, message) => {
  recentErrors.push({
    at: new Date().toISOString(),
    kind,
    message: String(message || '').slice(0, 300),
  });
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
};

/** Started once from main.js: an error ring buffer and the Firebase socket's state. */
export function installBugContext() {
  if (installed) return;
  installed = true;
  try {
    window.addEventListener('error', (e) => {
      note('error', e?.message || e?.error?.message || 'error');
    });
    window.addEventListener('unhandledrejection', (e) => {
      const r = e?.reason;
      note('rejection', r?.message || (typeof r === 'string' ? r : 'unhandled rejection'));
    });
  } catch { /* never fatal */ }
  try {
    if (db) {
      onValue(dbRef(db, '.info/connected'), (snap) => {
        connected = snap.val() === true;
        connectedChangedAt = new Date().toISOString();
      }, () => {});
    }
  } catch { /* never fatal */ }
}

// Test-only.
export const noteBugContextError = note;
export const resetBugContext = () => {
  recentErrors.length = 0;
  connected = null;
  connectedChangedAt = null;
};

const safe = (fn, fallback = null) => {
  try { return fn(); } catch { return fallback; }
};

// Mirrors App.vue's template: which top-level screen is showing.
const which = (state, getters) => {
  if (getters.isApproved === false && state.firebaseConfigured) return 'site-gate';
  if (state.game && state.mode === 'table') return 'table';
  if (state.game) return 'phone';
  if (state.roomId) return 'lobby';
  return 'landing';
};

/**
 * The context block for a bug report, from the Vuex store. Never throws.
 */
export function gatherBugContext(store) {
  const state = safe(() => store?.state, null) || {};
  const getters = safe(() => store?.getters, null) || {};
  const game = safe(() => state.game, null) || null;
  const ctx = {};

  ctx.screen = safe(() => which(state, getters));
  ctx.mode = safe(() => state.mode || null);
  ctx.online = safe(() => Boolean(state.roomId));
  ctx.roomId = safe(() => state.roomId || null);
  ctx.roomCode = safe(() => state.roomMeta?.roomCode || null);
  ctx.campaign = safe(() => state.roomMeta?.name || null);
  ctx.roomStatus = safe(() => state.roomMeta?.status || null);
  ctx.roomError = safe(() => (state.roomError ? String(state.roomError?.message || state.roomError).slice(0, 300) : null));
  ctx.isHost = safe(() => Boolean(getters.isHost));
  ctx.uid = safe(() => state.user?.uid || null);
  ctx.siteStatus = safe(() => state.siteUser?.status || null);
  ctx.isAdmin = safe(() => Boolean(getters.isAdmin));

  ctx.game = safe(() => (game ? {
    phase: game.meta?.phase ?? null,
    round: game.meta?.round ?? null,
    era: game.meta?.era ?? null,
    seq: game.meta?.seq ?? null,
    players: (game.players || []).length,
    playerNames: (game.players || []).map((p) => p.name || '?'),
    myPlayerId: getters.myPlayerId ?? null,
    myName: safe(() => getters.playerName(getters.myPlayerId)) || state.settings?.playerName || null,
    myRoles: getters.myRoles || [],
    isMyTurn: Boolean(getters.isMyTurn),
    // Who holds what, by NAME (seats are roles here).
    roles: Object.fromEntries(Object.entries(game.roles || {})
      .map(([role, id]) => [role, (game.players || []).find((p) => p.id === id)?.name || id])),
    hands: game.hands || null,
    done: game.done || null,
    eventCard: game.event?.cardId || null,
    goal: game.civ?.goal || null,
    result: game.result || null,
    logLength: (game.log || []).length,
  } : null));

  // Who is at the table right now, as names - never emails.
  ctx.presence = safe(() => {
    const p = state.presence || {};
    const names = Object.keys(p).map((uid) => (state.roomMeta?.playerSlots || [])
      .find((s) => s.uid === uid)?.displayName || uid);
    return names.length ? names : null;
  });
  ctx.seats = safe(() => (state.roomMeta?.playerSlots || [])
    .map((s) => `${s.displayName || '?'}${s.table ? ' (table)' : ''}`));

  ctx.viewingRole = safe(() => help.role || null);
  ctx.openDialogs = safe(() => {
    const open = [...document.querySelectorAll('[role="dialog"]')]
      .map((el) => el.getAttribute('aria-label') || el.className || 'dialog')
      .filter((label) => label !== 'Report a bug');
    return open.length ? open.slice(0, 6) : null;
  });
  ctx.settings = safe(() => state.settings || null);
  ctx.hasSavedGame = safe(() => Boolean(state.hasSavedGame));
  ctx.playtest = safe(() => (playtest.on || playtest.mic !== 'off'
    ? { on: playtest.on, mic: playtest.mic, sent: playtest.sent } : null));

  ctx.navigatorOnline = safe(() => navigator.onLine);
  ctx.firebaseConnected = connected;
  ctx.firebaseConnectedChangedAt = connectedChangedAt;
  ctx.queuedReports = safe(() => queuedBugReportCount());
  ctx.recentErrors = recentErrors.length ? recentErrors.slice() : null;

  ctx.buildTime = safe(() => process.env.VUE_APP_BUILD_TIME || null);
  ctx.visibility = safe(() => document.visibilityState);
  ctx.standalone = safe(() => Boolean(window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone));
  ctx.serviceWorker = safe(() => Boolean(navigator.serviceWorker?.controller));
  ctx.viewportHtml = safe(() => document.documentElement.dataset.viewport || null);
  ctx.orientation = safe(() => window.screen?.orientation?.type || null);
  ctx.language = safe(() => navigator.language || null);
  ctx.timezone = safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  ctx.reducedMotion = safe(() => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));

  return ctx;
}
